import type { ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { AlertCircle, CheckCircle2, Mail } from 'lucide-react';
import { useStores } from '@/hooks/useStores';
import { Button } from '@/components/Button';
import { useT } from '@/i18n/LocaleProvider';
import { registerEnglishArea } from '@/i18n/catalogue';
import { banners } from '@featit/shared/i18n/messages/en/banners';

registerEnglishArea('banners', banners);

/**
 * What the sign-in screen shows when the password was right and the address was never confirmed
 * (F-0160 AC-25, D-0041 = a) — and the one way forward from there without a session: a new link
 * (AC-25b).
 *
 * Blue, like the "session ended" note beside it, and not the red of a refusal: nothing the person
 * typed is wrong, and "wrong password" is exactly the misreading the server's `email_unverified`
 * code exists to prevent.
 *
 * The new link is asked for with the email and password **still in the form**, because the server
 * proves the account again before sending — there is no session to prove it with. The address shown
 * is the one the refusal was for; if the person edits the field afterwards, the next sign-in attempt
 * replaces this notice anyway.
 */
export const UnverifiedSignInNotice = observer(function UnverifiedSignInNotice({
  email,
  password,
}: {
  email: string;
  password: string;
}) {
  const t = useT();
  const { authStore } = useStores();
  const address = authStore.unverifiedSignInEmail ?? email;
  const state = authStore.verificationResend;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="unverified-sign-in"
      className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 text-[13px] text-blue-900 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-100"
    >
      <div className="flex items-start gap-2.5">
        <Mail className="mt-px h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0">
          <span className="font-medium">{t('banners.unverified.confirmFirst')}</span>
          <span className="mt-0.5 block">
            {t.rich('banners.unverified.sentTo', {
              email: address,
              address: (chunks: ReactNode) => (
                <span className="inline-block max-w-full align-bottom font-medium [overflow-wrap:anywhere]">
                  {chunks}
                </span>
              ),
            })}
          </span>
        </span>
      </div>

      <div className="mt-2.5 pl-[26px]">
        {state === 'sent' ? (
          <p className="flex items-start gap-1.5 text-blue-900 dark:text-blue-100">
            <CheckCircle2 className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{t('banners.unverified.newLinkSent')}</span>
          </p>
        ) : state === 'already-verified' ? (
          <p className="flex items-start gap-1.5 text-blue-900 dark:text-blue-100">
            <CheckCircle2 className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{t('banners.unverified.alreadyConfirmed')}</span>
          </p>
        ) : (
          <>
            {state === 'failed' && authStore.verificationResendError && (
              <p
                role="alert"
                className="mb-2 flex items-start gap-1.5 text-red-700 dark:text-red-300"
              >
                <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>{authStore.verificationResendError}</span>
              </p>
            )}
            {/* `md`: 44 px on a phone, where this notice is often read (I-0653). */}
            <Button
              type="button"
              size="md"
              loading={state === 'sending'}
              disabled={!email.trim() || !password}
              onClick={() => void authStore.resendVerificationForSignIn(email.trim(), password)}
            >
              {t('banners.unverified.sendNew')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
});
